import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";
import LanguageSwitcher from "@/components/luxenova/LanguageSwitcher";

export default function Navbar() {
    const { t } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const NAV = [
        { id: "home", labelKey: "nav.home", href: "#home" },
        { id: "property", labelKey: "nav.property", href: "#property" },
        { id: "concierge", labelKey: "nav.concierge", href: "#concierge" },
        { id: "events", labelKey: "nav.events", href: "#events" },
        { id: "journal", labelKey: "nav.journal", to: "/blog" },
        { id: "booking", labelKey: "nav.booking", href: "#booking" },
        { id: "contact", labelKey: "nav.contact", href: "#contact" },
    ];

    const go = (item) => {
        setOpen(false);
        if (item.to) {
            navigate(item.to);
            return;
        }
        if (location.pathname !== "/") {
            navigate("/" + item.href); // "/#contact"
            return;
        }
        const el = document.querySelector(item.href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled || location.pathname !== "/"
                    ? "bg-[#050D1C]/90 backdrop-blur-2xl border-b border-[#C9A96E]/15"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <button
                    data-testid="nav-logo"
                    onClick={() => (location.pathname === "/" ? go({ href: "#home" }) : navigate("/"))}
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={BRAND.logo}
                        alt="LuxeNova"
                        className="h-11 w-11 rounded-full object-cover ring-1 ring-[#C9A96E]/30 group-hover:ring-[#C9A96E]/70 transition-all"
                    />
                    <span className="hidden sm:inline font-serif-display text-white text-xl tracking-wide">
                        LuxeNova
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-6">
                    {NAV.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l)}
                            className="text-[12px] tracking-[0.16em] uppercase text-slate-300 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap"
                        >
                            {t(l.labelKey)}
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <LanguageSwitcher />
                    <button
                        data-testid="nav-cta-contact"
                        onClick={() => go({ href: "#contact" })}
                        className="btn-gold"
                    >
                        {t("nav.enquire")}
                    </button>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((s) => !s)}
                    className="lg:hidden text-slate-100 p-2"
                    aria-label="Menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden bg-[#050D1C]/95 backdrop-blur-xl border-t border-[#C9A96E]/15"
                >
                    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
                        {NAV.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-mobile-link-${l.id}`}
                                onClick={() => go(l)}
                                className="text-left text-slate-200 text-sm tracking-[0.22em] uppercase py-2 border-b border-white/5"
                            >
                                {t(l.labelKey)}
                            </button>
                        ))}
                        <div className="mt-2 flex items-center justify-between">
                            <LanguageSwitcher />
                            <button onClick={() => go({ href: "#contact" })} className="btn-gold">
                                {t("nav.enquire")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
